import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-triggered fade-in animations
 * @param {number} threshold - Intersection observer threshold (0-1)
 * @returns {Array} [ref setter, isVisible boolean]
 */
export const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [element, setElement] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!element) return;

    // Disconnect previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new intersection observer
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, disconnect observer to prevent re-triggering
          observerRef.current?.disconnect();
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element enters viewport
      }
    );

    observerRef.current.observe(element);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [element, threshold]);

  return [setElement, isVisible];
};

export default useScrollAnimation;
